import {
  ClientCommunicationsPhone,
  type IPolicyStore,
  type IPolicyStorePolicy,
} from './policyStore.model';
import { useGlobalStore } from '@/stores/global/global.store';

const isCanadaPolicyNumber = (policyNumber: string) =>
  policyNumber ? policyNumber.match(/^(05|10)\d+/) !== null : false;

const getClientCommunicationsPhone = (policy: IPolicyStorePolicy) => {
  if (!policy.contractId) {
    return '';
  } else if (isCanadaPolicyNumber(policy.contractId)) {
    if (policy.owner?.address.state === 'PQ') {
      return ClientCommunicationsPhone.CA_FR;
    }
    return ClientCommunicationsPhone.CA_EN;
  }
  return ClientCommunicationsPhone.US_EN;
};

export const hasTermLife = (state: IPolicyStore) => state.policies.length > 0;

export const currentPolicy = (state: IPolicyStore): IPolicyStorePolicy => {
  // Get the current policy based on the selectedPolicyIndex
  if (
    Array.isArray(state.policies) &&
    state.policies.length > 0 &&
    state.selectedPolicyIndex >= 0 &&
    state.selectedPolicyIndex < state.policies.length
  ) {
    return state.policies[state.selectedPolicyIndex];
  }
  return {} as IPolicyStorePolicy;
};

export const currentPaymentPanel = (state: IPolicyStore) =>
  state.paymentPanel[state.selectedPolicyIndex];

export const issueDateForCurrentPolicy = (state: IPolicyStore) => {
  const policy = currentPolicy(state);
  if (Array.isArray(policy.phases) && policy.phases.length > 0) {
    const phaseForIssueDateThatWeWant = policy.phases.filter((phase) => phase.person === '20');
    if (phaseForIssueDateThatWeWant.length > 0) {
      return phaseForIssueDateThatWeWant[0].issueDate;
    }
    return '';
  }
  return '';
};

export const panelValue = (state: IPolicyStore) => {
  const panelItem = currentPaymentPanel(state);
  return panelItem.panelValue;
};

export const policyOwner = (state: IPolicyStore) => {
  const policy = currentPolicy(state);
  if (policy.owner) {
    return policy.owner;
  } else {
    return null;
  }
};

export const activePrimaryCoverage = (state: IPolicyStore) =>
  state.policies.reduce((accumulator, policy) => accumulator + policy.activePrimaryCoverage, 0);

export const activeSpouseCoverage = (state: IPolicyStore) =>
  state.policies.reduce((accumulator, policy) => accumulator + policy.activeSpouseCoverage, 0);

export const isPending = (state: IPolicyStore | undefined) => {
  if (!state || state.policies.length === 0 || state.selectedPolicyIndex < 0) {
    return false;
  }
  const policy = currentPolicy(state);
  return policy.pending;
};

export const isRestricted = (state: IPolicyStore) => {
  const policy = currentPolicy(state);
  return !policy.data || policy.restricted;
};

export const isHomeOffice = (state: IPolicyStore) => {
  const globalStore = useGlobalStore();
  const policy = currentPolicy(state);
  const restricted = isRestricted(state);
  const agent = globalStore.getAgent(policy.agentId || '');
  return !restricted && agent?.isHomeOfficeAgent;
};

export const restrictCodeMessage = (state: IPolicyStore) => {
  // For policies with the following restrict codes, display the messages as indicated - see restrictions below
  //  1S
  //  1W
  //  1A
  //    No premiums are due or being collected, policy is on Waiver of Premium. For additional assistance or information,
  //    please contact our Claims department at >number varies depending on if US/NY/CAN<.
  //  0P
  //  1P
  //  0A
  //    This policy has a TIAB Claim in Progress, for additional assistance or information,
  //    please contact our Claims department at >number varies depending on if US/NY/CAN<.
  //  0H
  //    No message
  const policy = currentPolicy(state);
  switch (policy.restrictCode) {
    // case '1W':
    // case '1S':
    // case '1A':
    //   return 'L_restrict_code_message_WOP';
    // case '0P':
    // case '1P':
    // case '0A':
    //   return 'L_restrict_code_message_TIAB';
    case '0H':
    default:
      return '';
  }
};

export const clientCommunicationsPhone = (state: IPolicyStore) => {
  // number varies depending on if US/NY/CAN
  const policy = currentPolicy(state);
  return getClientCommunicationsPhone(policy);
};

export const policyCount = (state: IPolicyStore) => state.policies.length;

export const bankNameResponse = (state: IPolicyStore) => state.bankName;

export const paymentInfoLoading = (state: IPolicyStore) => state.loading.payment;

export const achSubmissionLoading = (state: IPolicyStore) => state.loading.ach;

export const policyInfoLoading = (state: IPolicyStore) => state.loading.policy;

export const beneficiariesLoading = (state: IPolicyStore) => state.loading.beneficiaries;

export const getPaymentMethodChangeAlert = (state: IPolicyStore) => {
  const paymentPanel = currentPaymentPanel(state);
  return paymentPanel.editPayments.paymentMethodChangeAlert;
};

export const getEditPaymentMethod = (state: IPolicyStore) => {
  const paymentPanel = currentPaymentPanel(state);
  return paymentPanel.editPayments.editPaymentMethod;
};

export const getDisablePaymentDetailsLink = (state: IPolicyStore) => {
  const paymentPanel = currentPaymentPanel(state);
  return paymentPanel.editPayments.disablePaymentDetailsLink;
};
export const showPayNow = (state: IPolicyStore) => {
  const globalStore = useGlobalStore();
  const policy = currentPolicy(state);
  return (
    globalStore.containsFeature('PAY-ONETIME') &&
    state.payNowLinkToggle &&
    !(!!policy.kubraPaymentStatus || policy.paymentInfo?.payInProcess) &&
    policy.showPayNowLink
  );
};

export const showPayNowFeatureFlagAndPayNowLinkToggle = (state: IPolicyStore) => {
  const globalStore = useGlobalStore();
  return globalStore.containsFeature('PAY-ONETIME') && state.payNowLinkToggle;
};

export const policiesSortedByDate = (state: IPolicyStore): IPolicyStorePolicy[] => {
  const pols: IPolicyStorePolicy[] = [];
  state.policies.forEach((policy) => {
    pols.push(policy);
  });
  pols.sort((a, b) => {
    const aDate = new Date(a.data?.issueDate ?? 0); // moment(a.data?.issueDate, 'YYYY-MM-DD');
    const bDate = new Date(b.data?.issueDate ?? 0); // moment(b.data?.issueDate, 'YYYY-MM-DD');
    return bDate.getTime() - aDate.getTime();
  });
  return pols;
};

export const beneficiariesTotalPrimaryPrimary = (state: IPolicyStore) =>
  state.beneficiaryPrimary.reduce((sum, bene) => {
    const percent = Number(bene.beneficiaryPercent || 0);
    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

export const beneficiariesTotalPrimaryContingent = (state: IPolicyStore) =>
  state.beneficiaryPrimaryContingent.reduce((sum, bene) => {
    const percent = Number(bene.beneficiaryPercent || 0);
    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

export const beneficiariesTotalSpousePrimary = (state: IPolicyStore) =>
  state.beneficiarySpouse.reduce((sum, bene) => {
    const percent = Number(bene.beneficiaryPercent || 0);
    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

export const beneficiariesTotalSpouseContingent = (state: IPolicyStore) =>
  state.beneficiarySpouseContingent.reduce((sum, bene) => {
    const percent = Number(bene.beneficiaryPercent || 0);
    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

export const showSpouseSection = (state: IPolicyStore) =>
  state.beneficiaries.filter((obj) => String(obj.beneficiaryType) === 'S').length > 0;

export const fetchDataForVAndTPrimaryPrimary = (state: IPolicyStore) =>
  state.beneficiaries.filter(
    (obj) => String(obj.beneficiaryType) === 'P' && (obj.origin === 'V' || obj.origin === 'T')
  );

export const fetchDataForVAndTPrimaryContingent = (state: IPolicyStore) =>
  state.beneficiaries.filter(
    (obj) => String(obj.beneficiaryType) === 'C' && (obj.origin === 'V' || obj.origin === 'T')
  );

export const fetchDataForVAndTSpousePrimary = (state: IPolicyStore) =>
  state.beneficiaries.filter(
    (obj) => String(obj.beneficiaryType) === 'S' && (obj.origin === 'V' || obj.origin === 'T')
  );
export const fetchDataForVAndTSpouseContingent = (state: IPolicyStore) =>
  state.beneficiaries.filter(
    (obj) => String(obj.beneficiaryType) === 'H' && (obj.origin === 'V' || obj.origin === 'T')
  );

export const beneficiaryHasOriginVorTWithIrrevocable = (state: IPolicyStore) =>
  state.beneficiaries.filter((obj) => obj.irrevocable && (obj.origin === 'V' || obj.origin === 'T'))
    .length > 0;

export const beneficiaryHasOriginVorT = (state: IPolicyStore) =>
  state.beneficiaries.filter((obj) => obj.origin === 'V' || obj.origin === 'T').length > 0;
